/**
 * PlanCameraFixturePage — a dev-only harness for card 24's plan camera
 * (`camera-feeds-page-plan-camera`, spec R14).
 *
 * ## Why a fixture page
 *
 * Staging's PUL-CON plan has eight cameras, every one holding a stream, and no camera behind a wall
 * — so the no-stream, lit, drop-target, 360° and wall-clipped looks cannot all be seen on a real
 * twin, and the lit and drop-target looks have no trigger in the app until `camera-feeds-page-stage`
 * and `camera-feeds-page-plan-drop` land. This page puts every look on one invented plan, with
 * controls for the two triggers, so a real browser can drive and measure them (jsdom lays nothing
 * out) and the captures can be taken on a fixed rig.
 *
 * Nothing here is real data and nothing is written anywhere: the stores are seeded synchronously
 * and no save is wired. As with `/link-repair-fixture`, the route exists only outside production
 * and the fixture constants live inside the component, so the bundle carries none of them.
 *
 * `?stress=1` swaps in 30 cameras and 300 walls, for the fixture probe's frame-time measurement.
 *
 * **Stream drag** (`camera-feeds-page-plan-drop`): the toggle hands the planner an `activeStreamDrag`
 * — *Reception*, a stream no camera holds, or *Track*, which `CAM-TRACK` holds — and a read-only
 * claim check that applies the Camera Feeds step's rule to this plan, so a dispatched `dragover`
 * draws every hint case: place, move, link, already showing, refused.
 */
import { useEffect, useMemo, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { TwinPlanner, plannerTheme } from '@repo/ui/planner';
import type { ActiveStreamDrag, StreamClaimCheck } from '@repo/ui/planner';
import { Tag } from '@repo/design-system';
import { useEditorStore, useFloorplanStore } from '@repo/front-end-state';
import { cameraDisplayName } from '@repo/front-end-utils';
import type {
  FloorPlannerCamera,
  FloorPlannerLayout,
  FloorPlannerWall,
} from '@repo/frontend-types';
import { semantic, space, typeRole } from '@repo/design-tokens';
import { PlanRendererProviders } from '../../components/3D/planView/PlanRendererProviders';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: ${semantic.surface.app};
`;
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[3]} ${space[4]};
`;
const Label = styled.span`
  color: ${semantic.text.secondary};
  font-size: ${typeRole.caption1.fontSize};
  margin-left: ${space[3]};
`;
const PlannerRegion = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;

const PlanCameraFixturePage = () => {
  const stress = useMemo(() => new URLSearchParams(window.location.search).has('stress'), []);
  const [toolset, setToolset] = useState<'cameras' | 'full'>('cameras');
  const [lit, setLit] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dragStream, setDragStream] = useState<'reception' | 'track'>('reception');
  const [signals, setSignals] = useState(0);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    const wall = (ax: number, ay: number, bx: number, by: number): FloorPlannerWall => ({
      a: { x: ax, y: ay },
      b: { x: bx, y: by },
      thickness: 0.2,
      openings: [],
    });
    const cam = (refid: string, over: Partial<FloorPlannerCamera>): FloorPlannerCamera => ({
      refid,
      x: 0,
      y: 0,
      rotation: 0,
      fovAngle: 100,
      detectionDistance: 8,
      rtspEndpointId: 1,
      ...over,
    });

    let cameras: FloorPlannerCamera[];
    let walls: FloorPlannerWall[];
    if (stress) {
      cameras = Array.from({ length: 30 }, (_u, i) =>
        cam(`CAM-S${i + 1}`, {
          name: `Stress ${i + 1}`,
          x: 2 + (i % 6) * 6,
          y: 2 + Math.floor(i / 6) * 5,
          rotation: (i * 37) % 360,
          rtspEndpointId: i % 4 === 0 ? null : i + 1,
        }),
      );
      walls = Array.from({ length: 300 }, (_u, i) => {
        const x = (i % 20) * 1.8;
        const y = Math.floor(i / 20) * 1.6;
        return i % 2 === 0 ? wall(x, y, x + 1.2, y) : wall(x, y, x, y + 1.1);
      });
    } else {
      walls = [
        wall(0, 0, 24, 0),
        wall(24, 0, 24, 14),
        wall(24, 14, 0, 14),
        wall(0, 14, 0, 0),
        // The interior wall that cuts Rack's cone short.
        wall(19, 5, 19, 10),
      ];
      cameras = [
        cam('CAM-TRACK', {
          name: 'Track',
          x: 4,
          y: 4,
          rotation: 30,
          fovAngle: 60,
          rtspEndpointId: 101,
        }),
        cam('CAM-GROUPEX', {
          name: 'Group Ex',
          x: 11,
          y: 11,
          rotation: 250,
          fovAngle: 53,
          detectionDistance: 9,
          rtspEndpointId: 102,
        }),
        cam('CAM-7', { name: 'Camera 7', x: 15, y: 3, rotation: 120, rtspEndpointId: null }),
        cam('CAM-LOBBY', {
          name: 'Lobby 360',
          x: 5,
          y: 11,
          fovAngle: 360,
          detectionDistance: 3,
          rtspEndpointId: 103,
        }),
        cam('CAM-RACK', {
          name: 'Rack',
          x: 15,
          y: 7.5,
          rotation: 0,
          detectionDistance: 9,
          rtspEndpointId: 104,
        }),
        cam('CAM-DOOR', {
          name: 'Door',
          x: 23.9,
          y: 13.4,
          rotation: 225,
          detectionDistance: 6,
          rtspEndpointId: 105,
        }),
      ];
    }
    const layout: FloorPlannerLayout = {
      name: 'Plan camera fixture',
      floors: [
        {
          id: 1,
          name: 'Ground Floor',
          level: 0,
          height: 3,
          designs: [{ name: 'Current', areas: [], surfaces: [], items: [], walls, cameras }],
        },
      ],
    };
    useFloorplanStore.getState().loadLayout(layout, 'FIXTURE', 'FIX', 'Plan camera fixture');
    useEditorStore.getState().clearHistory();
    useEditorStore.getState().clearModified();
    useEditorStore.setState({ selectedEntityIds: [stress ? 'CAM-S1' : 'CAM-TRACK'] });
    setSeeded(true);
    return () => {
      useFloorplanStore.getState().reset();
      useEditorStore.getState().setStreamDragActive(false);
    };
  }, [stress]);

  /*
   * The drag the planner previews. The planner owns `streamDragActive` from here: it follows this
   * prop, as it does on the Camera Feeds step.
   */
  const activeStreamDrag = useMemo<ActiveStreamDrag | null>(
    () =>
      !dragging
        ? null
        : dragStream === 'track'
          ? { rtspEndpointId: 101, label: 'Track' }
          : { rtspEndpointId: 200, label: 'Reception' },
    [dragging, dragStream],
  );
  /** The Camera Feeds step's claim rule, read-only, over this one-floor plan. */
  const onStreamClaimCheck = (endpointId: number, target: string | null): StreamClaimCheck => {
    const layout = useFloorplanStore.getState().layout;
    const cameras = (layout?.floors[0]?.designs[0]?.cameras ?? []).filter(c => !c._deleted);
    const named = (c: FloorPlannerCamera) => cameraDisplayName(c, cameras.indexOf(c));
    const onTarget = cameras.find(c => c.refid === target);
    const holder = cameras.find(c => c.refid !== target && c.rtspEndpointId === endpointId);
    const releaseFrom = holder ? named(holder) : null;
    // A camera showing another stream asks to replace it (`camera-feeds-page-link`), as the step does.
    if (onTarget && onTarget.rtspEndpointId != null && onTarget.rtspEndpointId !== endpointId) {
      return { replaceFrom: `Stream ${onTarget.rtspEndpointId}`, releaseFrom };
    }
    return { releaseFrom };
  };

  return (
    <PlanRendererProviders twinRecord={null} twinBid={undefined}>
      {/* Renderer-switch R3.2: this fixture draws in 3D, as onboarding does. No twin record: the
          anchor falls back as onboarding's does with none. */}
      <Page>
        <Controls data-testid="fixture-controls">
          <Label>Toolset</Label>
          {(['cameras', 'full'] as const).map(t => (
            <Tag
              key={t}
              surface="on-solid"
              toggle
              active={toolset === t}
              onToggle={() => setToolset(t)}
            >
              {t === 'cameras' ? 'Camera Feeds' : 'Floor Plan'}
            </Tag>
          ))}
          <Label>Lit</Label>
          <Tag surface="on-solid" toggle active={lit === null} onToggle={() => setLit(null)}>
            None
          </Tag>
          <Tag
            surface="on-solid"
            toggle
            active={lit === 'CAM-GROUPEX'}
            onToggle={() => setLit('CAM-GROUPEX')}
          >
            Group Ex
          </Tag>
          <Label>Stream drag</Label>
          <Tag surface="on-solid" toggle active={dragging} onToggle={() => setDragging(d => !d)}>
            {dragging ? 'On' : 'Off'}
          </Tag>
          {(['reception', 'track'] as const).map(k => (
            <Tag
              key={k}
              surface="on-solid"
              toggle
              active={dragStream === k}
              onToggle={() => setDragStream(k)}
            >
              {k === 'reception' ? 'Reception (not placed)' : 'Track (held)'}
            </Tag>
          ))}
          <Label data-testid="fixture-signals">Camera edits signalled: {signals}</Label>
        </Controls>
        <PlannerRegion data-testid="fixture-planner">
          {seeded && (
            <ThemeProvider theme={plannerTheme}>
              <TwinPlanner
                key={toolset}
                toolset={toolset}
                showToolbar={false}
                showFullscreenButton={false}
                showPropertiesPanel={false}
                litCameraRefid={lit}
                // As the Camera Feeds step draws it: an unlinked camera is work to do there only.
                unlinkedCamerasAreWork={toolset === 'cameras'}
                activeStreamDrag={activeStreamDrag}
                onStreamClaimCheck={onStreamClaimCheck}
                onStreamClaim={() => 'The fixture places nothing.'}
                onStreamLinked={() => undefined}
                onCameraEdited={() => setSignals(n => n + 1)}
              />
            </ThemeProvider>
          )}
        </PlannerRegion>
      </Page>
    </PlanRendererProviders>
  );
};

export default PlanCameraFixturePage;
